import React, { useRef, useState, useEffect } from 'react';

interface TiltedCardProps {
  imageSrc: string;
  altText?: string;
  containerHeight?: string;
  containerWidth?: string;
  imageHeight?: string;
  imageWidth?: string;
  scaleOnHover?: number;
  rotateAmplitude?: number;
  className?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'scale-down';
}

const TiltedCard: React.FC<TiltedCardProps> = ({
  imageSrc,
  altText = 'Tilted card image',
  containerHeight = '400px',
  containerWidth = '100%',
  imageHeight = '100%',
  imageWidth = '100%',
  scaleOnHover = 1.05,
  rotateAmplitude = 8,
  className = '',
  objectFit = 'contain'
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [transform, setTransform] = useState('');

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isHovered) return;
      
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = ((y - centerY) / centerY) * -rotateAmplitude;
      const rotateY = ((x - centerX) / centerX) * rotateAmplitude;
      
      setTransform(
        `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${scaleOnHover})`
      );
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      setTransform('perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)');
    };

    const handleMouseEnter = () => {
      setIsHovered(true);
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);
    card.addEventListener('mouseenter', handleMouseEnter);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
      card.removeEventListener('mouseenter', handleMouseEnter);
    };
  }, [isHovered, scaleOnHover, rotateAmplitude]);

  return (
    <div
      ref={cardRef}
      className={`relative overflow-hidden cursor-pointer ${className}`}
      style={{
        height: containerHeight,
        width: containerWidth,
        transformStyle: 'preserve-3d'
      }}
    >
      <div
        className="w-full h-full transition-transform duration-200 ease-out"
        style={{
          transform: transform || 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)',
          transformStyle: 'preserve-3d'
        }}
      >
        <img
          src={imageSrc}
          alt={altText}
          className={`w-full h-full ${
            objectFit === 'contain' ? 'object-contain' :
            objectFit === 'cover' ? 'object-cover' :
            objectFit === 'fill' ? 'object-fill' :
            'object-scale-down'
          }`}
          style={{
            height: imageHeight,
            width: imageWidth,
            objectFit: objectFit
          }}
        />
      </div>
    </div>
  );
};

export default TiltedCard;